<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  subjects: {
    type: Array,
    required: true
  },
  modelValue: {
    type: Array,
    default: () => []
  },
  errors: {
    type: Object,
    default: () => ({})
  },
  title: {
    type: String,
    default: 'Teaching Subjects'
  },
  description: {
    type: String,
    default: 'Select all subjects you\'re qualified and comfortable teaching'
  }
})

const emit = defineEmits(['update:modelValue'])

const localErrors = ref({})
const searchQuery = ref('')

const displayErrors = computed(() => ({
  ...props.errors,
  ...localErrors.value
}))

const filteredSubjects = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.subjects
  }

  const query = searchQuery.value.toLowerCase().trim()
  return props.subjects.filter(subject =>
    subject.name.toLowerCase().includes(query) ||
    (subject.description && subject.description.toLowerCase().includes(query))
  )
})

const toggleSubject = (id) => {
  const selected = [...props.modelValue]
  const index = selected.indexOf(id)

  if (index === -1) {
    selected.push(id)
  } else {
    selected.splice(index, 1)
  }

  emit('update:modelValue', selected)

  // Clear error when user makes a selection
  if (localErrors.value.selected_subjects) {
    delete localErrors.value.selected_subjects
  }
}

const isSelected = (id) => props.modelValue.includes(id)

const validate = () => {
  const errors = {}

  if (props.modelValue.length === 0) {
    errors.selected_subjects = 'Please select at least one subject'
  }

  localErrors.value = errors
  return Object.keys(errors).length === 0
}

defineExpose({ validate })
</script>

<template>
  <div class="bg-white rounded-2xl p-10">
    <div class="mb-8">
      <h2 class="text-lg md:text-2xl font-bold text-gray-900 mb-2">
        {{ title }} <span class="text-red-500">*</span>
      </h2>
      <p class="text-gray-600">{{ description }}</p>

      <!-- Error Message -->
      <div v-if="displayErrors.selected_subjects" class="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
        <div class="flex items-start gap-3">
          <span class="material-symbols-outlined text-red-600 text-xl mt-0.5">error</span>
          <div>
            <p class="text-red-600 font-semibold text-base">{{ displayErrors.selected_subjects }}</p>
            <p class="text-red-700 text-sm mt-1">Choose at least one subject from the list below.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Filter -->
    <div class="mb-6">
      <div class="relative">
        <div class="absolute left-4 top-1/2 transform -translate-y-1/2">
          <span class="material-symbols-outlined text-gray-400 text-2xl">search</span>
        </div>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search subjects..."
          class="w-full pl-14 pr-5 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900 text-base"
        />
        <button
          v-if="searchQuery"
          type="button"
          @click="searchQuery = ''"
          class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <span class="material-symbols-outlined text-xl">close</span>
        </button>
      </div>
      <p v-if="searchQuery && filteredSubjects.length === 0" class="text-sm text-gray-500 mt-2 flex items-center">
        <span class="material-symbols-outlined text-sm mr-1">info</span>
        No subjects found matching "{{ searchQuery }}".
      </p>
      <p v-else-if="searchQuery" class="text-sm text-gray-500 mt-2">
        Showing {{ filteredSubjects.length }} of {{ subjects.length }} subjects
      </p>
    </div>

    <!-- Simple View - Compact grid for settings -->
    <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
      <button
        v-for="subject in filteredSubjects"
        :key="subject.id"
        type="button"
        @click="toggleSubject(subject.id)"
        class="relative group px-4 py-3 rounded-lg border-2 transition-all hover:shadow-sm"
        :class="isSelected(subject.id) ? '' : 'border-gray-200 bg-white hover:border-gray-300'"
        :style="{
          borderColor: isSelected(subject.id) ? subject.color : '',
          backgroundColor: isSelected(subject.id) ? `${subject.color}08` : ''
        }"
      >
        <span class="text-sm font-semibold text-gray-900 text-center block">{{ subject.name }}</span>

        <!-- Triangle indicator for selected subjects -->
        <div
          v-if="isSelected(subject.id)"
          class="absolute top-0 right-0 w-0 h-0"
          :style="{
            borderTop: `20px solid ${subject.color}`,
            borderLeft: '20px solid transparent'
          }"
        ></div>
      </button>
    </div>
  </div>
</template>
